import { Bot, BotStatsDataPoint } from '../../../../models/bot';
import { TimeGranularity } from '../../../../models/paymentSummary';
import { bucketKey, enumerateBuckets } from './formatBucket';

export const DEFAULT_TREND_N = 5;

export const SERIES_PALETTE = [
    '#1976d2',
    '#2e7d32',
    '#ed6c02',
    '#7b1fa2',
    '#c62828',
    '#00838f',
    '#5d4037',
    '#c2185b',
    '#1565c0',
    '#558b2f',
    '#ef6c00',
    '#6a1b9a',
];

const FAIL_RATE_THRESHOLD = 20;
const FAIL_RATE_MIN_N = 10;
const VOLUME_DROP_MIN_N = 10;
const VOLUME_DROP_RATIO = 0.5;
const MAX_IDLE_ANOMALIES = 10;

export type BotRow = {
    botId: number;
    label: string;
    successes: number;
    failures: number;
    total: number;
    sharePercent: number;
    successRate: number;
    firstHalfTotal: number;
    secondHalfTotal: number;
};

export type TimeRow = {
    x: Date;
    [key: string]: Date | number;
};

export type AnomalyKind = 'highFailRate' | 'zeroLoad' | 'volumeDrop';

export type Anomaly = {
    botId?: number;
    label: string;
    kind: AnomalyKind;
    message: string;
};

export type TrendMetric = 'total' | 'success' | 'failure' | 'successRate';

export function botLabel(botId: number, names: Map<number, string>): string {
    const name = names.get(botId);
    return name ? `#${botId} · ${name}` : `#${botId}`;
}

export function buildBotNames(bots: Bot[]): Map<number, string> {
    const map = new Map<number, string>();
    for (const b of bots) {
        const name = (b.nickName || b.userName || '').trim();
        if (name) {
            map.set(b.id, name);
        }
    }
    return map;
}

export function colorForIndex(idx: number): string {
    return SERIES_PALETTE[idx % SERIES_PALETTE.length];
}

function calculatePercent(part: number, total: number): number {
    if (!total) {
        return 0;
    }
    return (part / total) * 100;
}

export type AggregatedBotStats = {
    rows: BotRow[];
    timeRows: TimeRow[];
    colors: Map<number, string>;
    anomalies: Anomaly[];
    grandTotal: number;
};

export function aggregateBotStats(args: {
    points: BotStatsDataPoint[];
    bots: Bot[];
    fromDate: string;
    toDate: string;
    granularity: TimeGranularity;
}): AggregatedBotStats {
    const { points, bots, fromDate, toDate, granularity } = args;
    const names = buildBotNames(bots);
    const allowedIds = bots.length > 0 ? new Set(bots.map((b) => b.id)) : null;
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const mid = new Date((from.getTime() + to.getTime()) / 2);

    const totals = new Map<number, { successes: number; failures: number; firstHalf: number; secondHalf: number }>();

    const ensure = (botId: number) => {
        let row = totals.get(botId);
        if (!row) {
            row = { successes: 0, failures: 0, firstHalf: 0, secondHalf: 0 };
            totals.set(botId, row);
        }
        return row;
    };

    for (const p of points) {
        if (allowedIds && !allowedIds.has(p.botId)) {
            continue;
        }
        const t = new Date(p.bucketUtc);
        if (t < from || t > to) {
            continue;
        }
        const row = ensure(p.botId);
        const value = p.value ?? 0;
        if (p.success) {
            row.successes += value;
        } else {
            row.failures += value;
        }
        if (t < mid) {
            row.firstHalf += value;
        } else {
            row.secondHalf += value;
        }
    }

    for (const b of bots) {
        ensure(b.id);
    }

    let grandTotal = 0;
    const rows: BotRow[] = [];
    totals.forEach((t, botId) => {
        const total = t.successes + t.failures;
        grandTotal += total;
        rows.push({
            botId,
            label: botLabel(botId, names),
            successes: t.successes,
            failures: t.failures,
            total,
            sharePercent: 0,
            successRate: calculatePercent(t.successes, total),
            firstHalfTotal: t.firstHalf,
            secondHalfTotal: t.secondHalf,
        });
    });

    rows.sort((a, b) => b.total - a.total || a.botId - b.botId);
    for (const row of rows) {
        row.sharePercent = calculatePercent(row.total, grandTotal);
    }

    const colors = new Map<number, string>();
    rows.forEach((row, idx) => {
        colors.set(row.botId, colorForIndex(idx));
    });

    const buckets = enumerateBuckets(from, to, granularity);
    const perBotBucket = new Map<number, Map<string, { success: number; failure: number }>>();
    for (const p of points) {
        if (allowedIds && !allowedIds.has(p.botId)) {
            continue;
        }
        const t = new Date(p.bucketUtc);
        if (t < from || t > to) {
            continue;
        }
        let botMap = perBotBucket.get(p.botId);
        if (!botMap) {
            botMap = new Map();
            perBotBucket.set(p.botId, botMap);
        }
        const key = bucketKey(t, granularity);
        let cell = botMap.get(key);
        if (!cell) {
            cell = { success: 0, failure: 0 };
            botMap.set(key, cell);
        }
        if (p.success) {
            cell.success += p.value ?? 0;
        } else {
            cell.failure += p.value ?? 0;
        }
    }

    const botIds = rows.map((r) => r.botId);
    const timeRows: TimeRow[] = buckets.map(({ key, date }) => {
        const row: TimeRow = { x: date };
        for (const botId of botIds) {
            const cell = perBotBucket.get(botId)?.get(key);
            const success = cell?.success ?? 0;
            const failure = cell?.failure ?? 0;
            const total = success + failure;
            row[`${botId}_success`] = success;
            row[`${botId}_failure`] = failure;
            row[`${botId}_total`] = total;
            row[`${botId}_rate`] = calculatePercent(success, total);
        }
        return row;
    });

    const anomalies: Anomaly[] = [];
    for (const row of rows) {
        if (row.total >= FAIL_RATE_MIN_N && 100 - row.successRate > FAIL_RATE_THRESHOLD) {
            anomalies.push({
                botId: row.botId,
                label: row.label,
                kind: 'highFailRate',
                message: `${row.label}: ошибок ${(100 - row.successRate).toFixed(1)}% (${row.failures} из ${row.total})`,
            });
        }
        if (
            row.firstHalfTotal >= VOLUME_DROP_MIN_N &&
            row.secondHalfTotal < row.firstHalfTotal * VOLUME_DROP_RATIO
        ) {
            anomalies.push({
                botId: row.botId,
                label: row.label,
                kind: 'volumeDrop',
                message: `${row.label}: нагрузка упала ${row.firstHalfTotal.toLocaleString('ru-RU')} → ${row.secondHalfTotal.toLocaleString('ru-RU')}`,
            });
        }
    }

    if (grandTotal > 0) {
        const idle = rows.filter((r) => r.total === 0);
        const shown = idle.slice(0, MAX_IDLE_ANOMALIES);
        for (const row of shown) {
            anomalies.push({
                botId: row.botId,
                label: row.label,
                kind: 'zeroLoad',
                message: `${row.label}: нет обменов за период`,
            });
        }
        if (idle.length > shown.length) {
            anomalies.push({
                kind: 'zeroLoad',
                label: 'Остальные',
                message: `ещё ${idle.length - shown.length} ботов без обменов за период`,
            });
        }
    }

    return {
        rows,
        timeRows,
        colors,
        anomalies,
        grandTotal,
    };
}

export function trendDataKey(botId: number, metric: TrendMetric): string {
    switch (metric) {
        case 'success':
            return `${botId}_success`;
        case 'failure':
            return `${botId}_failure`;
        case 'successRate':
            return `${botId}_rate`;
        case 'total':
        default:
            return `${botId}_total`;
    }
}
